import { Injectable, OnDestroy, inject } from '@angular/core';
import { BehaviorSubject, forkJoin, Observable, of, Subject } from 'rxjs';
import { Inventory, InventoryMeta, PublishInventoryInput } from '../inventory.model';
import { InventoryValidationIssue, ValidationStatus } from '../../common/model/validation-status.model';
import { AppState, Dictionary, ProjectKey } from '../../model/reducer';
import { select, Store } from '@ngrx/store';
import { allInventoriesView, inventoryKeyView, inventoryMetaView } from '../../model/views';
import { first, map, mapTo, switchMap, takeUntil, tap, withLatestFrom } from 'rxjs/operators';
import _ from 'lodash';
import { LoadDiffData, RequestPublishInventory } from '../../model/publish-inventory/publish-inventory.actions';
import { InventoryService } from '../inventory.service';
import { ModalNotificationService } from '../../notification/modal-notification.service';
import { NotificationMessage } from '../../notification/notification.model';
import { PublishInventoryPayload } from './publish-inventory-payload.model';

@Injectable()
export class PublishInventoryContext implements OnDestroy {
  private store$ = inject<Store<AppState>>(Store);
  private inventoryApi = inject(InventoryService);
  private modalNotificationService = inject(ModalNotificationService);

  private _inventoryKeySnapshot$: BehaviorSubject<string | null> = new BehaviorSubject(null);
  private _currentBranchSnapshot$: BehaviorSubject<string | null> = new BehaviorSubject(null);
  private _inventoryMetaSnapshot$: BehaviorSubject<InventoryMeta | null> = new BehaviorSubject(null);
  private _inventoryValidationStatusSnapshot$: BehaviorSubject<ValidationStatus<InventoryValidationIssue> | null> = new BehaviorSubject(null);

  private destroyed$: Subject<boolean> = new Subject<boolean>();

  inventoryKey$: Observable<ProjectKey | null> = this._inventoryKeySnapshot$.asObservable();
  currentBranch$: Observable<string | null> = this._currentBranchSnapshot$.asObservable();
  inventoryMeta$: Observable<InventoryMeta | null> = this._inventoryMetaSnapshot$.asObservable();
  inventoryValidationStatus$: Observable<ValidationStatus<InventoryValidationIssue> | null> = this._inventoryValidationStatusSnapshot$.asObservable();

  constructor() {
    this.store$.pipe(select(inventoryKeyView), first((inventoryKey: string | null) => !_.isNil(inventoryKey)), takeUntil(this.destroyed$))
      .subscribe((inventoryKey: string) => this._inventoryKeySnapshot$.next(inventoryKey));

    this.store$.pipe(select(inventoryMetaView), first((inventoryMeta: InventoryMeta | null) => !_.isNil(inventoryMeta)), takeUntil(this.destroyed$))
      .subscribe((inventoryMeta: InventoryMeta) => this._inventoryMetaSnapshot$.next(inventoryMeta));

    forkJoin([this.store$.pipe(select(allInventoriesView), first()), this.inventoryKey$.pipe(first())]).pipe(
      map(([inventories, inventoryKey]: [Dictionary<Inventory>, string]) => {
        const inventory: Inventory | undefined = inventories[inventoryKey];
        return _.isNil(inventory) ? null : inventory.branch || null;
      }),
      takeUntil(this.destroyed$)
    ).subscribe((branch: string | null) => this._currentBranchSnapshot$.next(branch));
  }

  preloadDiffs(): void {
    this.store$.dispatch(new LoadDiffData());
  }

  private checkAndConfirmExistingTags(newTags: string[]): Observable<boolean> {
    if (newTags.length > 0) {
      return this.inventoryKey$.pipe(
        switchMap((inventoryKey: string): Observable<string[]> => this.inventoryApi.getAllTagsOfInventory(inventoryKey)),
        switchMap((allTags: string[]): Observable<boolean> => {
          const conflictingTags: string[] = newTags.filter(newTag => allTags.includes(newTag));
          if (conflictingTags.length > 0) {
            let notification: NotificationMessage;
            if (conflictingTags.length === 1) {
              notification = {
                title: 'Tag already exists',
                description: `The tag <strong>${conflictingTags[0]}</strong> already exists. Do you want to overwrite it?`
              };
            } else {
              const tagsLabel: string = conflictingTags.map(tag => `<strong>${tag}</strong>`).join(', ');
              notification = {
                title: 'Tags already exist',
                description: `The tags ${tagsLabel} already exist. Do you want to overwrite them?`
              };
            }
            return this.modalNotificationService.openConfirmDialog(notification, {
              confirmButtonText: 'Overwrite tags',
              cancelButtonText: 'Cancel',
            }).afterClosed().pipe(map((confirmed?: boolean) => {
              return !!confirmed;
            }));
          } else {
            return of(true);
          }
        })
      );
    }
    return of(true);
  }

  private publishInventoryChanges(publishInventoryInput: PublishInventoryInput): Observable<void> {
    return this.inventoryMeta$.pipe(
      first((inventoryMeta: InventoryMeta | null) => !_.isNil(inventoryMeta)),
      withLatestFrom(this.inventoryKey$, this.store$.pipe(select(allInventoriesView))),
      takeUntil(this.destroyed$),
      tap(([inventoryMeta, inventoryKey, inventories]: [InventoryMeta, string | null, Dictionary<Inventory>]): void => {
        const inventoryToPublish: Inventory | undefined = _.isNil(inventoryKey) ? undefined : inventories[inventoryKey];
        if (_.isNil(inventoryToPublish)) {
          return;
        }
        const publishRequest: PublishInventoryPayload = {
          inventoryMeta,
          commitMessage: publishInventoryInput.commitMessage,
          inventory: inventoryToPublish,
          tags: publishInventoryInput.tags,
        } as any;
        this.store$.dispatch(new RequestPublishInventory(publishRequest));
      }),
      mapTo(undefined),
    );
  }

  requestPublishInventoryChanges(publishInventoryInput: PublishInventoryInput): Observable<boolean> {
    return this.checkAndConfirmExistingTags(publishInventoryInput.tags).pipe(
      switchMap((confirmed: boolean): Observable<boolean> => {
        if (confirmed) {
          return this.publishInventoryChanges(publishInventoryInput).pipe(mapTo(true));
        } else {
          return of(false);
        }
      }),
    );
  }

  ngOnDestroy(): void {
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }
}
